import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Drawer,
  Descriptions,
  Divider,
  Spin,
} from "antd";
import { motion } from "framer-motion";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/block/Block";
import { ImpactMetricCard } from "./components/ImpactMetricCard";
import { FinancialImpactCard } from "./components/FinancialImpactCard";
import { AlertFlagBadge } from "./components/AlertFlagBadge";
import {
  ShimmerMetricCard,
  ShimmerTableCard,
} from "./components/ProcurementShimmerLoader";
import { formatCurrency } from "../../utils/Utils";
import {
  DollarOutlined,
  ClockCircleOutlined,
  StopOutlined,
  WarningOutlined,
  BellOutlined,
  RobotOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

interface ProcurementRisk {
  key: string;
  material: string;
  delayType: string;
  delayDays: number | string;
  projectsAffected: number;
  tasksAffected: number;
  wosAffected: number;
  riskValue: number;
  riskSource: string;
  flag: "🔴" | "🟠" | "🟢";
}

interface TaskImpact {
  key: string;
  project: string;
  taskId: string;
  taskName: string;
  plannedStart: string;
  actualStart: string;
  materialDependency: string;
  materialStatus: string;
  delayDays: number | string;
  riskValue: number;
  flag: "🔴" | "🟠" | "🟢";
}

interface WorkOrderImpact {
  key: string;
  woNumber: string;
  contractor: string;
  linkedTasks: string;
  materialCausingDelay: string;
  delayDays: number;
  executedQty: string;
  pendingQty: string;
  raValueImpact: number;
  flag: "🔴" | "🟠" | "🟢";
}

interface MaterialRiskInterlinking {
  materialId: string;
  materialName: string;
  delayDays: number;
  projectsAffected: number;
  tasksAffected: number;
  wosAffected: number;
  riskType: string;
  downstreamImpact: string;
  triggeredModule: string;
  monetaryImpact: number;
  automatedMitigation: string;
  automatedMitigationSteps?: Array<{
    step: string;
    details?: string[];
    suppliers?: Array<{ name: string; onTimePercentage: number }>;
    siteTransfers?: Array<{
      siteName: string;
      availableQty: string;
      distance: string;
      transferTime: string;
    }>;
    notifications?: string[];
    costSavings?: string;
  }>;
  responsiblePerson: string;
  status: "Active" | "Mitigated" | "Pending";
}

interface InvoicePayableRisk {
  key: string;
  invoiceNumber: string;
  poNumber: string;
  supplier: string;
  dueDate: string;
  invoiceValue: number;
  outstandingValue: number;
  agingDays: number;
  interestRate: number; // 2% per month
  interestCharged: number; // (agingDays / 30) × 2%
  riskImpact: number; // Outstanding Value × (interestCharged / 100)
  flag: "🔴" | "🟠" | "🟢";
}

const ProcurementInventoryControlTower: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedRisk, setSelectedRisk] = useState<ProcurementRisk | null>(
    null
  );
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [
    materialInterlinkingDrawerVisible,
    setMaterialInterlinkingDrawerVisible,
  ] = useState(false);
  const [selectedMaterialRisks, setSelectedMaterialRisks] = useState<
    MaterialRiskInterlinking[]
  >([]);
  const [invoiceRiskDrawerVisible, setInvoiceRiskDrawerVisible] =
    useState(false);
  const [selectedInvoiceRisk, setSelectedInvoiceRisk] =
    useState<InvoicePayableRisk | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [aiLoadingMaterials, setAiLoadingMaterials] = useState<{
    [key: string]: boolean;
  }>({});
  const [aiLoadingTexts, setAiLoadingTexts] = useState<{
    [key: string]: string;
  }>({});

  // Mock data
  const procurementRisks: ProcurementRisk[] = [
    {
      key: "1",
      material: "Cement OPC 53",
      delayType: "Delivery Delay",
      delayDays: 5,
      projectsAffected: 3,
      tasksAffected: 8,
      wosAffected: 4,
      riskValue: 780000,
      riskSource: "UltraTech Cement Ltd",
      flag: "🔴",
    },
    {
      key: "2",
      material: "8mm Rebar",
      delayType: "Stock Shortage",
      delayDays: 10,
      projectsAffected: 2,
      tasksAffected: 6,
      wosAffected: 2,
      riskValue: 1240000,
      riskSource: "Tata Steel Limited",
      flag: "🔴",
    },
    {
      key: "3",
      material: "Paint Primer",
      delayType: "Quality Rejection",
      delayDays: "---",
      projectsAffected: 1,
      tasksAffected: 3,
      wosAffected: 1,
      riskValue: 210000,
      riskSource: "Asian Paints Ltd",
      flag: "🟠",
    },
    {
      key: "4",
      material: "Electrical Cable",
      delayType: "Pending PO",
      delayDays: "---",
      projectsAffected: 1,
      tasksAffected: 2,
      wosAffected: 1,
      riskValue: 180000,
      riskSource: "Procurex Technologies",
      flag: "🟠",
    },
    {
      key: "5",
      material: "Sand",
      delayType: "Stock Under Safety",
      delayDays: 3,
      projectsAffected: 2,
      tasksAffected: 4,
      wosAffected: 1,
      riskValue: 360000,
      riskSource: "Inventory Shortage",
      flag: "🟠",
    },
  ];

  const taskImpacts: TaskImpact[] = [
    {
      key: "1",
      project: "Grandeur Heights",
      taskId: "T001",
      taskName: "Foundation Work",
      plannedStart: "03/05/2025",
      actualStart: "---",
      materialDependency: "Cement OPC 53",
      materialStatus: "Delayed (5d)",
      delayDays: 5,
      riskValue: 225000,
      flag: "🔴",
    },
    {
      key: "2",
      project: "Paramount Rise",
      taskId: "T015",
      taskName: "Masonry - 1st Floor",
      plannedStart: "03/12/2025",
      actualStart: "---",
      materialDependency: "Rebar 8mm",
      materialStatus: "Shortage (10d)",
      delayDays: 10,
      riskValue: 420000,
      flag: "🔴",
    },
    {
      key: "3",
      project: "Elysium Towers",
      taskId: "T023",
      taskName: "Plastering",
      plannedStart: "03/18/2025",
      actualStart: "03/19/2025",
      materialDependency: "Sand",
      materialStatus: "Low Stock",
      delayDays: 3,
      riskValue: 75000,
      flag: "🟠",
    },
    {
      key: "4",
      project: "Skyline Royale",
      taskId: "T044",
      taskName: "Electrical Wiring",
      plannedStart: "03/25/2025",
      actualStart: "---",
      materialDependency: "Cable",
      materialStatus: "PO Pending",
      delayDays: "---",
      riskValue: 120000,
      flag: "🟠",
    },
  ];

  const workOrderImpacts: WorkOrderImpact[] = [
    {
      key: "1",
      woNumber: "WO-2025-0012",
      contractor: "ABC Constructions",
      linkedTasks: "Foundation & RCC",
      materialCausingDelay: "Cement, Rebar",
      delayDays: 7,
      executedQty: "65%",
      pendingQty: "35%",
      raValueImpact: 810000,
      flag: "🔴",
    },
    {
      key: "2",
      woNumber: "WO-2025-0045",
      contractor: "DEF Interiors",
      linkedTasks: "Electrical Works",
      materialCausingDelay: "Cable",
      delayDays: 3,
      executedQty: "45%",
      pendingQty: "55%",
      raValueImpact: 190000,
      flag: "🟠",
    },
    {
      key: "3",
      woNumber: "WO-2025-0067",
      contractor: "XYZ Infra",
      linkedTasks: "Masonry",
      materialCausingDelay: "Sand",
      delayDays: 5,
      executedQty: "50%",
      pendingQty: "50%",
      raValueImpact: 375000,
      flag: "🟠",
    },
  ];

  const invoicePayableRisks: InvoicePayableRisk[] = [
    {
      key: "1",
      invoiceNumber: "INV-2025-0234",
      poNumber: "PO-2025-0089",
      supplier: "Steel Suppliers Ltd",
      dueDate: "2025-02-15",
      invoiceValue: 1250000,
      outstandingValue: 1250000,
      agingDays: 25,
      interestRate: 2, // 2% per month
      // Interest charged = (25 / 30) × 2% = 1.67%
      interestCharged: 1.67,
      // Risk Impact = 1,250,000 × (1.67 / 100) = ₹20,875
      riskImpact: 20875,
      flag: "🔴",
    },
    {
      key: "2",
      invoiceNumber: "INV-2025-0198",
      poNumber: "PO-2025-0067",
      supplier: "Cement Corporation",
      dueDate: "2025-02-20",
      invoiceValue: 850000,
      outstandingValue: 850000,
      agingDays: 20,
      interestRate: 2,
      // Interest charged = (20 / 30) × 2% = 1.33%
      interestCharged: 1.33,
      // Risk Impact = 850,000 × (1.33 / 100) = ₹11,305
      riskImpact: 11305,
      flag: "🔴",
    },
    {
      key: "3",
      invoiceNumber: "INV-2025-0312",
      poNumber: "PO-2025-0123",
      supplier: "Electrical Distributors",
      dueDate: "2025-03-01",
      invoiceValue: 450000,
      outstandingValue: 450000,
      agingDays: 12,
      interestRate: 2,
      // Interest charged = (12 / 30) × 2% = 0.80%
      interestCharged: 0.8,
      // Risk Impact = 450,000 × (0.80 / 100) = ₹3,600
      riskImpact: 3600,
      flag: "🟠",
    },
    {
      key: "4",
      invoiceNumber: "INV-2025-0278",
      poNumber: "PO-2025-0098",
      supplier: "Paint & Coating Solutions",
      dueDate: "2025-02-28",
      invoiceValue: 320000,
      outstandingValue: 320000,
      agingDays: 15,
      interestRate: 2,
      // Interest charged = (15 / 30) × 2% = 1.00%
      interestCharged: 1.0,
      // Risk Impact = 320,000 × (1.00 / 100) = ₹3,200
      riskImpact: 3200,
      flag: "🟠",
    },
    {
      key: "5",
      invoiceNumber: "INV-2025-0345",
      poNumber: "PO-2025-0145",
      supplier: "Hardware Supplies Inc",
      dueDate: "2025-03-10",
      invoiceValue: 180000,
      outstandingValue: 180000,
      agingDays: 5,
      interestRate: 2,
      // Interest charged = (5 / 30) × 2% = 0.33%
      interestCharged: 0.33,
      // Risk Impact = 180,000 × (0.33 / 100) = ₹594
      riskImpact: 594,
      flag: "🟢",
    },
  ];

  const aiLoadingMessages = [
    "Analyzing material dependencies...",
    "Checking site inventory levels...",
    "Fetching optimal solutions...",
    "Processing constraints...",
    "Calculating impact...",
    "Generating recommendations...",
  ];

  const mockMaterialRiskData: {
    [materialKey: string]: MaterialRiskInterlinking[];
  } = {
    "Cement OPC 53": [
      {
        materialId: "M-001",
        materialName: "Cement OPC 53",
        delayDays: 5,
        projectsAffected: 3,
        tasksAffected: 8,
        wosAffected: 4,
        riskType: "Material Shortage",
        downstreamImpact: "Delay in Foundation & RCC Work",
        triggeredModule: "Procurement & Work Order",
        monetaryImpact: 780000,
        automatedMitigation:
          "Expedite supply from alternate supplier + Site-to-Site Transfer from Villa Cluster site",
        automatedMitigationSteps: [
          {
            step: "Check Site-to-Site Transfer Availability",
            siteTransfers: [
              {
                siteName: "Villa Cluster - Site B",
                availableQty: "450 bags (22.5 tons)",
                distance: "12 km",
                transferTime: "Same day (4-6 hours)",
              },
              {
                siteName: "Mall Fitout - Site C",
                availableQty: "280 bags (14 tons)",
                distance: "8 km",
                transferTime: "Same day (3-4 hours)",
              },
            ],
            details: [
              "Total Required at Tower A: 850 bags",
              "Available via Transfer: 730 bags (sufficient for 3 days)",
              "Cost Savings: ₹18,500 (no urgent procurement premium + reduced logistics cost)",
            ],
          },
          {
            step: "Initiate Inter-Site Material Transfer",
            details: [
              "Transfer from Villa Cluster - Site B: 450 bags",
              "Transfer Logistics: Internal fleet (2 trucks, 6-ton capacity)",
              "Estimated Arrival: Today by 5 PM",
              "Material Release Authorization: Auto-approved (within same project portfolio)",
            ],
            notifications: [
              "Notify Site B Store Manager - Release 450 bags Cement OPC 53",
              "Notify Tower A Site Engineer - Expect delivery by 5 PM today",
              "Update inventory system - Inter-site transfer logged",
            ],
          },
          {
            step: "Expedite Supplier Purchase for Balance Quantity",
            details: [
              "Balance Quantity Needed: 120 bags (after site transfer)",
              "PR Number: PR-1045 (Updated)",
              "Need-by Date: 2025-10-14 (revised from 2025-10-17)",
            ],
            suppliers: [
              { name: "UltraTech Cement Ltd.", onTimePercentage: 97 },
              { name: "ACC Cement (Ambuja Group)", onTimePercentage: 95 },
            ],
          },
          {
            step: "Stakeholder Notifications",
            notifications: [
              "Notify Amit Sharma (Procurement Manager) - Site transfer + balance PO",
              "Alert Project Manager (Tower A) - Material arrangement confirmed",
              "Update WO-2025-0012 Contractor - Work can proceed as scheduled",
            ],
            costSavings:
              "₹18,500 (avoided urgent procurement + optimized inventory utilization)",
          },
        ],
        responsiblePerson: "Amit Sharma",
        status: "Active",
      },
    ],
    "8mm Rebar": [
      {
        materialId: "M-002",
        materialName: "8mm Rebar (TMT Steel)",
        delayDays: 10,
        projectsAffected: 2,
        tasksAffected: 6,
        wosAffected: 2,
        riskType: "Material Shortage",
        downstreamImpact: "Delay in Structural Work & Masonry",
        triggeredModule: "Procurement & Work Order",
        monetaryImpact: 1240000,
        automatedMitigation:
          "Site Transfer from Road Infra + Expedite alternate supplier",
        automatedMitigationSteps: [
          {
            step: "Check Site-to-Site Transfer Availability",
            siteTransfers: [
              {
                siteName: "Road Infra Project - Site D",
                availableQty: "6.5 tons (surplus stock)",
                distance: "18 km",
                transferTime: "1 day",
              },
            ],
            details: [
              "Total Required at Tower B: 15 tons",
              "Available via Transfer: 6.5 tons (43% coverage)",
              "Balance to Procure: 8.5 tons",
              "Cost Savings: ₹32,500 (optimized surplus utilization)",
            ],
          },
          {
            step: "Initiate Inter-Site Material Transfer",
            details: [
              "Transfer from Road Infra - Site D: 6.5 tons (13 bundles)",
              "Transfer Logistics: Hired truck (12-ton capacity)",
              "Estimated Arrival: Tomorrow by 10 AM",
              "Material Release: Requires Project Manager approval (different project)",
            ],
            notifications: [
              "Request approval from Vikram Singh (PM - Road Infra) for 6.5 tons transfer",
              "Notify Site D Store Manager - Prepare 13 bundles for dispatch",
              "Alert Tower B Site Engineer - Expect delivery by 10 AM tomorrow",
            ],
          },
          {
            step: "Expedite Supplier Purchase for Balance Quantity",
            details: [
              "Balance Quantity: 8.5 tons",
              "PR Number: PR-1087 (Auto-generated)",
              "Need-by Date: 2025-10-22",
            ],
            suppliers: [
              { name: "Shree TMT Industries", onTimePercentage: 98 },
              { name: "Jindal TMT Bars", onTimePercentage: 96 },
              { name: "TATA Tiscon TMT", onTimePercentage: 94 },
            ],
          },
          {
            step: "Stakeholder Notifications & Approvals",
            notifications: [
              "Escalate to PMO for inter-project transfer approval",
              "Notify Procurement Head - Dual-track mitigation (transfer + procurement)",
              "Alert WO-2025-0045 Contractor - Partial material available tomorrow",
            ],
            costSavings:
              "₹32,500 (surplus reallocation + avoided full urgent procurement)",
          },
        ],
        responsiblePerson: "Desai (Procurement Head)",
        status: "Active",
      },
    ],
    Sand: [
      {
        materialId: "M-003",
        materialName: "M-Sand (Construction Grade)",
        delayDays: 3,
        projectsAffected: 2,
        tasksAffected: 4,
        wosAffected: 1,
        riskType: "Material Shortage",
        downstreamImpact: "Delay in Plastering & Masonry Work",
        triggeredModule: "Procurement",
        monetaryImpact: 360000,
        automatedMitigation: "Auto-generate PR + Site transfer if needed",
        automatedMitigationSteps: [
          {
            step: "Check Site-to-Site Transfer Feasibility",
            siteTransfers: [
              {
                siteName: "Warehouse B",
                availableQty: "45 cubic meters",
                distance: "15 km",
                transferTime: "Same day",
              },
            ],
            details: [
              "Total Required: 85 cubic meters",
              "Available via Transfer: 45 cubic meters (53% coverage)",
              "Balance to Procure: 40 cubic meters",
            ],
          },
          {
            step: "Trigger Auto Purchase Request",
            details: [
              "PR Number: PR-1089 (Auto-generated)",
              "Material: M-Sand (Construction Grade)",
              "Quantity: 85 cubic meters (full requirement - no transfer due to low cost)",
              "Need-by Date: 2025-10-25",
              "Preferred Supplier: M-Sand Suppliers Co. (92% On-Time)",
            ],
          },
          {
            step: "Stakeholder Notifications",
            notifications: [
              "Auto-notify Procurement Team for PR approval",
              "Trigger RFQ to 3 pre-approved sand suppliers",
              "Alert site teams about expected delivery timeline",
            ],
          },
        ],
        responsiblePerson: "Procurement Team",
        status: "Pending",
      },
    ],
    "Rebar 8mm": [
      {
        materialId: "M-002",
        materialName: "8mm Rebar (TMT Steel)",
        delayDays: 10,
        projectsAffected: 2,
        tasksAffected: 6,
        wosAffected: 2,
        riskType: "Material Shortage",
        downstreamImpact: "Delay in Structural Work & Masonry",
        triggeredModule: "Procurement & Work Order",
        monetaryImpact: 1240000,
        automatedMitigation:
          "Site Transfer from Road Infra + Expedite alternate supplier",
        automatedMitigationSteps: [
          {
            step: "Check Site-to-Site Transfer Availability",
            siteTransfers: [
              {
                siteName: "Road Infra Project - Site D",
                availableQty: "6.5 tons (surplus stock)",
                distance: "18 km",
                transferTime: "1 day",
              },
            ],
            details: [
              "Total Required at Tower B: 15 tons",
              "Available via Transfer: 6.5 tons (43% coverage)",
              "Balance to Procure: 8.5 tons",
              "Cost Savings: ₹32,500 (optimized surplus utilization)",
            ],
          },
          {
            step: "Initiate Inter-Site Material Transfer",
            details: [
              "Transfer from Road Infra - Site D: 6.5 tons (13 bundles)",
              "Transfer Logistics: Hired truck (12-ton capacity)",
              "Estimated Arrival: Tomorrow by 10 AM",
              "Material Release: Requires Project Manager approval (different project)",
            ],
            notifications: [
              "Request approval from Vikram Singh (PM - Road Infra) for 6.5 tons transfer",
              "Notify Site D Store Manager - Prepare 13 bundles for dispatch",
              "Alert Tower B Site Engineer - Expect delivery by 10 AM tomorrow",
            ],
          },
          {
            step: "Expedite Supplier Purchase for Balance Quantity",
            details: [
              "Balance Quantity: 8.5 tons",
              "PR Number: PR-1087 (Auto-generated)",
              "Need-by Date: 2025-10-22",
            ],
            suppliers: [
              { name: "Shree TMT Industries", onTimePercentage: 98 },
              { name: "Jindal TMT Bars", onTimePercentage: 96 },
              { name: "TATA Tiscon TMT", onTimePercentage: 94 },
            ],
          },
          {
            step: "Stakeholder Notifications & Approvals",
            notifications: [
              "Escalate to PMO for inter-project transfer approval",
              "Notify Procurement Head - Dual-track mitigation (transfer + procurement)",
              "Alert WO-2025-0045 Contractor - Partial material available tomorrow",
            ],
            costSavings:
              "₹32,500 (surplus reallocation + avoided full urgent procurement)",
          },
        ],
        responsiblePerson: "Desai (Procurement Head)",
        status: "Active",
      },
    ],
    Cable: [
      {
        materialId: "M-004",
        materialName: "Electrical Cable (XLPE 4-core)",
        delayDays: 3,
        projectsAffected: 1,
        tasksAffected: 2,
        wosAffected: 1,
        riskType: "Material Shortage",
        downstreamImpact: "Delay in Electrical Wiring Works",
        triggeredModule: "Procurement",
        monetaryImpact: 180000,
        automatedMitigation:
          "Expedite PO approval + Check inter-site availability",
        automatedMitigationSteps: [
          {
            step: "Expedite Pending Purchase Order",
            details: [
              "PO Number: PO-2025-089 (Pending approval)",
              "Material: XLPE 4-core Cable (1.5 sq mm)",
              "Quantity: 850 meters",
              "Supplier: Polycab Wires Pvt. Ltd.",
              "Current Status: Awaiting buyer approval",
              "Action: Fast-track approval process",
            ],
            notifications: [
              "Escalate to Buying Manager for immediate approval",
              "Notify supplier to keep stock ready for dispatch",
              "Alert site team about expected delivery post-approval",
            ],
          },
          {
            step: "Check Site-to-Site Transfer as Backup",
            siteTransfers: [
              {
                siteName: "Villa Cluster - Site B",
                availableQty: "320 meters (partial coverage)",
                distance: "12 km",
                transferTime: "Same day",
              },
            ],
            details: [
              "Total Required: 850 meters",
              "Available via Transfer: 320 meters (38% coverage)",
              "Note: Transfer only if PO approval delayed beyond 2 days",
            ],
          },
        ],
        responsiblePerson: "Procurement Team",
        status: "Pending",
      },
    ],
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 1500);
  }, []);

  const procurementRiskColumns = [
    {
      title: "Material",
      dataIndex: "material",
      key: "material",
      width: 150,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Delay Type",
      dataIndex: "delayType",
      key: "delayType",
      width: 130,
    },
    {
      title: "Delay (Days)",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      align: "center" as const,
      render: (val: number | string, record: ProcurementRisk) =>
        val !== "---" ? <Tag color="red">{val}</Tag> : <span>---</span>,
    },
    {
      title: "Projects",
      dataIndex: "projectsAffected",
      key: "projectsAffected",
      width: 80,
      align: "center" as const,
    },
    {
      title: "Tasks",
      dataIndex: "tasksAffected",
      key: "tasksAffected",
      width: 80,
      align: "center" as const,
      render: (val: number) => (
        <Tag color={val > 5 ? "red" : "orange"} style={{ fontWeight: 600 }}>
          {val}
        </Tag>
      ),
    },
    {
      title: "WOs",
      dataIndex: "wosAffected",
      key: "wosAffected",
      width: 70,
      align: "center" as const,
    },
    {
      title: "Risk Impact",
      dataIndex: "riskValue",
      key: "riskValue",
      width: 120,
      align: "right" as const,
      render: (val: number, record: any) => (
        <motion.span
          style={{
            fontWeight: 700,
            color: "#ff4d4f",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          onClick={(e) => {
            e.stopPropagation();
            const materialRisks = mockMaterialRiskData[record.material] || [];
            setSelectedMaterialRisks(materialRisks);
            setMaterialInterlinkingDrawerVisible(true);
          }}
        >
          {formatCurrency(val, "INR")}
        </motion.span>
      ),
    },
    {
      title: "Risk Source",
      dataIndex: "riskSource",
      key: "riskSource",
      width: 130,
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: "🔴" | "🟠" | "🟢") => <AlertFlagBadge flag={flag} />,
    },
  ];

  const taskImpactColumns = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 120,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Task ID",
      dataIndex: "taskId",
      key: "taskId",
      width: 80,
    },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 150,
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStart",
      key: "plannedStart",
      width: 110,
      render: (text: string) => (
        <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
      ),
      align: "center" as const,
    },
    {
      title: "Actual Start",
      dataIndex: "actualStart",
      key: "actualStart",
      width: 110,
      render: (text: string) => (
        <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
      ),
      align: "center" as const,
    },
    {
      title: "Material Dependency",
      dataIndex: "materialDependency",
      key: "materialDependency",
      width: 150,
    },
    {
      title: "Material Status",
      dataIndex: "materialStatus",
      key: "materialStatus",
      width: 140,
      render: (text: string) => {
        const isDelayed = text.includes("Delayed") || text.includes("Shortage");
        return (
          <Tag color={isDelayed ? "red" : "orange"} style={{ fontSize: 11 }}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Delay (Days)",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      align: "center" as const,
      render: (val: number | string, record: TaskImpact) =>
        val !== "---" ? <Tag color="red">{val}</Tag> : <span>---</span>,
    },
    {
      title: "Risk Impact",
      dataIndex: "riskValue",
      key: "riskValue",
      width: 120,
      align: "right" as const,
      render: (val: number, record: any) => (
        <span
          style={{
            fontWeight: 600,
            color: "#ff4d4f",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={(e) => {
            e.stopPropagation();
            const materialRisks =
              mockMaterialRiskData[record.materialDependency] || [];
            setSelectedMaterialRisks(materialRisks);
            setMaterialInterlinkingDrawerVisible(true);
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: "🔴" | "🟠" | "🟢") => <AlertFlagBadge flag={flag} />,
    },
  ];

  const workOrderImpactColumns = [
    {
      title: "WO #",
      dataIndex: "woNumber",
      key: "woNumber",
      width: 120,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 150,
    },
    {
      title: "Linked Tasks",
      dataIndex: "linkedTasks",
      key: "linkedTasks",
      width: 140,
    },
    {
      title: "Material Causing Delay",
      dataIndex: "materialCausingDelay",
      key: "materialCausingDelay",
      width: 150,
    },
    {
      title: "Delay (Days)",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      align: "center" as const,
      render: (val: number, record: WorkOrderImpact) => (
        <Tag color="red">{val}</Tag>
      ),
    },
    {
      title: "Executed Work %",
      dataIndex: "executedQty",
      key: "executedQty",
      width: 100,
      align: "center" as const,
    },
    {
      title: "Pending Work %",
      dataIndex: "pendingQty",
      key: "pendingQty",
      width: 100,
      align: "center" as const,
    },
    {
      title: "Work Value Impact",
      dataIndex: "raValueImpact",
      key: "raValueImpact",
      width: 130,
      align: "right" as const,
      render: (val: number, record: WorkOrderImpact) => (
        <span
          style={{
            fontWeight: 600,
            color: "#ff4d4f",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Use first material if multiple materials are listed
            const firstMaterial = record.materialCausingDelay
              .split(",")[0]
              .trim();
            const materialRisks = mockMaterialRiskData[firstMaterial] || [];
            setSelectedMaterialRisks(materialRisks);
            setMaterialInterlinkingDrawerVisible(true);
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: "🔴" | "🟠" | "🟢") => <AlertFlagBadge flag={flag} />,
    },
  ];

  const controlFlags = [
    {
      key: "1",
      flag: "🔴",
      trigger: "Material Delay >7 days or Stock Shortage >20%",
      action: "Escalate to Procurement Head, freeze new WOs",
    },
    {
      key: "2",
      flag: "🟠",
      trigger: "Delay 3-6 days",
      action: "Auto-generate PR for shortage",
    },
    {
      key: "3",
      flag: "🟢",
      trigger: "No dependency delay",
      action: "Continue as per plan",
    },
    {
      key: "4",
      flag: "⚫",
      trigger: "Deferred Billing > ₹10L",
      action: "Notify CFO to reforecast cashflow",
    },
  ];

  const invoicePayableColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 130,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "PO #",
      dataIndex: "poNumber",
      key: "poNumber",
      width: 120,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      width: 180,
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 120,
      align: "center" as const,
      render: (text: string) => (
        <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
      ),
    },
    {
      title: "Invoice Value (₹)",
      dataIndex: "invoiceValue",
      key: "invoiceValue",
      width: 140,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Outstanding Value (₹)",
      dataIndex: "outstandingValue",
      key: "outstandingValue",
      width: 160,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 700, color: "#cf1322" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Aging Days",
      dataIndex: "agingDays",
      key: "agingDays",
      width: 110,
      align: "center" as const,
      render: (val: number) => (
        <Tag color={val > 20 ? "red" : val > 10 ? "orange" : "green"}>
          {val} days
        </Tag>
      ),
    },
    {
      title: "Interest On Delay",
      dataIndex: "interestCharged",
      key: "interestCharged",
      width: 140,
      align: "center" as const,
      render: (val: number, record: InvoicePayableRisk) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Tag color="orange">{record.interestRate}% per month</Tag>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#fa8c16" }}>
            {val.toFixed(2)}% charged
          </span>
        </div>
      ),
    },
    {
      title: "Risk Impact (₹)",
      dataIndex: "riskImpact",
      key: "riskImpact",
      width: 130,
      align: "right" as const,
      render: (val: number, record: InvoicePayableRisk) => (
        <span
          style={{
            fontWeight: 700,
            color: "#ff4d4f",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedInvoiceRisk(record);
            setInvoiceRiskDrawerVisible(true);
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => <span style={{ fontSize: 18 }}>{flag}</span>,
    },
  ];

  return (
    <>
      <Head title="Procurement & Inventory Risk Impact Control Tower" />
      <Content>
        <BlockHead size="lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <BlockHeadContent>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BlockTitle tag="h4">
                  Procurement & Inventory Risk Impact Control Tower
                </BlockTitle>
              </motion.div>
              <motion.div
                className="nk-block-des"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <p>
                  Real-time risk tracing from procurement to project execution
                  with monetary exposure analysis
                </p>
              </motion.div>
            </BlockHeadContent>
          </motion.div>
        </BlockHead>

        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {/* Executive View Summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Executive View
                </h5>
                {loading ? (
                  <Row gutter={[16, 16]}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <Col xs={24} sm={12} lg={8} key={i}>
                        <ShimmerMetricCard delay={i * 0.1} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={4}>
                      <ImpactMetricCard
                        title="Delayed Deliveries"
                        value={18}
                        suffix="materials"
                        valueColor="#ff4d4f"
                        flag="red"
                        threshold=">10% of total"
                        delay={0.4}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                      <ImpactMetricCard
                        title="Stockout Materials"
                        value={7}
                        suffix="items"
                        valueColor="#faad14"
                        flag="orange"
                        threshold=">5%"
                        delay={0.45}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                      <ImpactMetricCard
                        title="Tasks Affected"
                        value={26}
                        suffix="tasks"
                        valueColor="#ff4d4f"
                        flag="red"
                        threshold=">10 tasks"
                        delay={0.5}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                      <ImpactMetricCard
                        title="Work Orders Affected"
                        value={11}
                        suffix="WOs"
                        valueColor="#ff4d4f"
                        flag="red"
                        threshold=">5 WOs"
                        delay={0.55}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                      <ImpactMetricCard
                        title="Outstanding Value"
                        value={invoicePayableRisks.reduce(
                          (sum, inv) => sum + inv.outstandingValue,
                          0
                        )}
                        prefix="₹"
                        valueColor="#cf1322"
                        flag="red"
                        threshold=">₹30L"
                        delay={0.6}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={4}>
                      <ImpactMetricCard
                        title="Risk Impact"
                        value={
                          procurementRisks.reduce(
                            (sum, risk) => sum + risk.riskValue,
                            0
                          ) +
                          invoicePayableRisks.reduce(
                            (sum, inv) => sum + inv.riskImpact,
                            0
                          )
                        }
                        prefix="₹"
                        valueColor="#ff4d4f"
                        flag="red"
                        threshold=">₹25L"
                        delay={0.65}
                      />
                    </Col>
                  </Row>
                )}
              </motion.div>

              <Divider />

              {/* Procurement Risk Register */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Procurement Risk Register (with Impact Mapping)
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={5} />
                ) : (
                  <Table
                    columns={procurementRiskColumns}
                    dataSource={procurementRisks}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1200 }}
                    // onRow={(record) => ({
                    //   onClick: () => {
                    //     setSelectedRisk(record);
                    //     setDrawerVisible(true);
                    //   },
                    //   style: { cursor: "pointer" },
                    // })}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Invoice Payables Risk */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Invoice Payables Risk
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={5} />
                ) : (
                  <Table
                    columns={invoicePayableColumns}
                    dataSource={invoicePayableRisks}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1400 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Task Impact Analysis */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Task Impact Analysis (Project Management Link)
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={4} />
                ) : (
                  <Table
                    columns={taskImpactColumns}
                    dataSource={taskImpacts}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1400 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Work Order Impact Analysis */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Work Order Impact Analysis
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={workOrderImpactColumns}
                    dataSource={workOrderImpacts}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1300 }}
                  />
                )}
              </motion.div>

              {/* Aggregated Financial Impact Summary */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  💰 Aggregated Financial Impact Summary
                </h5>
                {loading ? (
                  <Row gutter={[16, 16]}>
                    {[0, 1, 2, 3].map((i) => (
                      <Col xs={24} sm={12} lg={6} key={i}>
                        <ShimmerMetricCard delay={i * 0.1} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                      <FinancialImpactCard
                        impactType="Direct Delay Cost"
                        value={820000}
                        description="Idle labor & equipment cost"
                        icon={<ClockCircleOutlined />}
                        color="#ff4d4f"
                        delay={1.35}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <FinancialImpactCard
                        impactType="Indirect Cost"
                        value={350000}
                        description="Overhead for unproductive period"
                        icon={<StopOutlined />}
                        color="#faad14"
                        delay={1.4}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <FinancialImpactCard
                        impactType="Deferred RA Billing"
                        value={1260000}
                        description="Work unable to bill due to delay"
                        icon={<DollarOutlined />}
                        color="#ff7a45"
                        delay={1.45}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <FinancialImpactCard
                        impactType="Total Financial Exposure"
                        value={2430000}
                        description="Combined risk exposure"
                        icon={<WarningOutlined />}
                        color="#cf1322"
                        delay={1.5}
                      />
                    </Col>
                  </Row>
                )}
              </motion.div> */}

              <Divider />

              {/* Business Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8 }}
                style={{
                  marginTop: 32,
                  // padding: "20px 24px",
                  // background: "#fff",
                  // borderRadius: 8,
                  // border: "1px solid #e8e8e8",
                }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Business Summary
                </h5>

                {/* Overview - Connected Flow */}
                <div
                  style={{
                    marginBottom: 24,
                    padding: "24px",
                    background:
                      "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
                    borderRadius: 8,
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Metric 1 */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: "50%",
                          background: "#fff",
                          border: "4px solid #ff4d4f",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: "#cf1322",
                          }}
                        >
                          12
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#595959",
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        Active Material Risks{" "}
                      </div>
                    </div>

                    {/* Connector */}
                    {/* <div style={{ fontSize: 20, color: "#bfbfbf" }}>▶</div> */}

                    {/* Metric 2 */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: "50%",
                          background: "#fff",
                          border: "4px solid #faad14",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: "#fa8c16",
                          }}
                        >
                          26
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#595959",
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        Tasks Impacted
                      </div>
                    </div>

                    {/* Connector */}
                    {/* <div style={{ fontSize: 20, color: "#bfbfbf" }}>▶</div> */}

                    {/* Metric 3 */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: "50%",
                          background: "#fff",
                          border: "4px solid #faad14",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: "#fa8c16",
                          }}
                        >
                          11
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#595959",
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        Work Orders Delayed
                      </div>
                    </div>

                    {/* Connector */}
                    {/* <div style={{ fontSize: 20, color: "#bfbfbf" }}>▶</div> */}

                    {/* Metric 4 */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: "50%",
                          background: "#fff",
                          border: "4px solid #ff4d4f",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: "#cf1322",
                          }}
                        >
                          ₹24.3L
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#595959",
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        Daily Financial Exposure
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Risk Materials */}
                <div style={{ marginBottom: 20 }}>
                  <h6
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 12,
                      color: "#262626",
                    }}
                  >
                    Top Risk Materials
                  </h6>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "#fff1f0",
                        borderRadius: 6,
                        border: "1px solid #ffccc7",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>🔴</span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#262626",
                          }}
                        >
                          Cement OPC 53
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                          5 days delay
                        </span>
                        <Tag color="red" style={{ fontWeight: 600, margin: 0 }}>
                          ₹7.8L Impact
                        </Tag>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "#fff1f0",
                        borderRadius: 6,
                        border: "1px solid #ffccc7",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>🔴</span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#262626",
                          }}
                        >
                          8mm Rebar (TMT Steel)
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                          10 days delay
                        </span>
                        <Tag color="red" style={{ fontWeight: 600, margin: 0 }}>
                          ₹12.4L Impact
                        </Tag>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "#fff7e6",
                        borderRadius: 6,
                        border: "1px solid #ffd591",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>🟠</span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#262626",
                          }}
                        >
                          Electrical Cable (XLPE 4-core)
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                          3 days delay
                        </span>
                        <Tag
                          color="orange"
                          style={{ fontWeight: 600, margin: 0 }}
                        >
                          ₹4.1L Impact
                        </Tag>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Immediate Mitigation */}
                <div>
                  <h6
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 12,
                      color: "#262626",
                    }}
                  >
                    Immediate Mitigation
                  </h6>

                  {/* Cement Mitigation */}
                  <div
                    style={{
                      marginBottom: 16,
                      paddingBottom: 16,
                      borderBottom: "1px dashed #e8e8e8",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 10,
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>🔴</span>
                      <strong style={{ fontSize: 13, color: "#262626" }}>
                        Cement OPC 53 - Expedite Alternate Supply
                      </strong>
                    </div>

                    <div style={{ marginLeft: 24, marginBottom: 12 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          marginBottom: 6,
                        }}
                      >
                        Suggested Suppliers (Ranked by On-Time Performance)
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            background: "#f0f5ff",
                            borderRadius: 4,
                            border: "1px solid #d6e4ff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#262626",
                            }}
                          >
                            1. UltraTech Cement Ltd.
                          </span>
                          <Tag
                            color="green"
                            style={{ fontWeight: 600, margin: 0 }}
                          >
                            97% On-Time
                          </Tag>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            background: "#f0f5ff",
                            borderRadius: 4,
                            border: "1px solid #d6e4ff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#262626",
                            }}
                          >
                            2. ACC Cement (Ambuja Group)
                          </span>
                          <Tag
                            color="green"
                            style={{ fontWeight: 600, margin: 0 }}
                          >
                            95% On-Time
                          </Tag>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            background: "#f0f5ff",
                            borderRadius: 4,
                            border: "1px solid #d6e4ff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#262626",
                            }}
                          >
                            3. Shree Cement Industries
                          </span>
                          <Tag
                            color="blue"
                            style={{ fontWeight: 600, margin: 0 }}
                          >
                            93% On-Time
                          </Tag>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginLeft: 24 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          marginBottom: 6,
                        }}
                      >
                        Actions & Notifications
                      </div>
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Expedite PO with UltraTech - Target delivery by
                          2025-10-17
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Notify Amit Sharma (Procurement Manager) - High
                          Priority
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Escalate to Desai (Procurement Head) if not confirmed
                          by 2025-10-12
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Rebar Mitigation */}
                  <div
                    style={{
                      marginBottom: 16,
                      paddingBottom: 16,
                      borderBottom: "1px dashed #e8e8e8",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 10,
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>🔴</span>
                      <strong style={{ fontSize: 13, color: "#262626" }}>
                        8mm Rebar - Expedite Alternate Supply
                      </strong>
                    </div>

                    <div style={{ marginLeft: 24, marginBottom: 12 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          marginBottom: 6,
                        }}
                      >
                        Suggested Suppliers (Ranked by On-Time Performance)
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            background: "#f0f5ff",
                            borderRadius: 4,
                            border: "1px solid #d6e4ff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#262626",
                            }}
                          >
                            1. Shree TMT Industries
                          </span>
                          <Tag
                            color="green"
                            style={{ fontWeight: 600, margin: 0 }}
                          >
                            98% On-Time
                          </Tag>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            background: "#f0f5ff",
                            borderRadius: 4,
                            border: "1px solid #d6e4ff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#262626",
                            }}
                          >
                            2. Jindal TMT Bars
                          </span>
                          <Tag
                            color="green"
                            style={{ fontWeight: 600, margin: 0 }}
                          >
                            96% On-Time
                          </Tag>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "6px 12px",
                            background: "#f0f5ff",
                            borderRadius: 4,
                            border: "1px solid #d6e4ff",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#262626",
                            }}
                          >
                            3. TATA Tiscon TMT (TATA Steel)
                          </span>
                          <Tag
                            color="blue"
                            style={{ fontWeight: 600, margin: 0 }}
                          >
                            94% On-Time
                          </Tag>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginLeft: 24 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          marginBottom: 6,
                        }}
                      >
                        Actions & Notifications
                      </div>
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Expedite PO with Shree TMT - Target delivery by
                          2025-10-20
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Update PR-1024 with revised need-by date
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Alert Project Manager about revised timeline
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Sand Auto-PR */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 10,
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>🟡</span>
                      <strong style={{ fontSize: 13, color: "#262626" }}>
                        M-Sand - Trigger Auto Purchase Request
                      </strong>
                    </div>

                    <div style={{ marginLeft: 24, marginBottom: 12 }}>
                      <ul
                        style={{
                          margin: 0,
                          padding: 0,
                          listStyle: "disc",
                          paddingLeft: 16,
                        }}
                      >
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          PR Number: PR-1089 (Auto-generated)
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          Material: M-Sand (Construction Grade)
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          Quantity Required: 85 cubic meters
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          Need-by Date: 2025-10-25
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 4,
                            lineHeight: 1.5,
                          }}
                        >
                          Preferred Supplier: M-Sand Suppliers Co. (92% On-Time)
                        </li>
                      </ul>
                    </div>

                    <div style={{ marginLeft: 24 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#595959",
                          marginBottom: 6,
                        }}
                      >
                        Actions & Notifications
                      </div>
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Auto-notify Procurement Team for approval
                        </li>
                        <li
                          style={{
                            fontSize: 12,
                            color: "#595959",
                            marginBottom: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <BellOutlined
                            style={{ fontSize: 12, color: "#1890ff" }}
                          />
                          Trigger RFQ to 3 pre-approved sand suppliers
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </Card>
      </Content>

      {/* Risk Details Drawer */}
      <Drawer
        title="Risk Details"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={600}
      >
        {selectedRisk && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Material">
                {selectedRisk.material}
              </Descriptions.Item>
              <Descriptions.Item label="Delay Type">
                {selectedRisk.delayType}
              </Descriptions.Item>
              <Descriptions.Item label="Delay Days">
                {selectedRisk.delayDays}
              </Descriptions.Item>
              <Descriptions.Item label="Projects Affected">
                {selectedRisk.projectsAffected}
              </Descriptions.Item>
              <Descriptions.Item label="Tasks Affected">
                {selectedRisk.tasksAffected}
              </Descriptions.Item>
              <Descriptions.Item label="Work Orders Affected">
                {selectedRisk.wosAffected}
              </Descriptions.Item>
              <Descriptions.Item label="Risk Value">
                {formatCurrency(selectedRisk.riskValue, "INR")}
              </Descriptions.Item>
              <Descriptions.Item label="Risk Source">
                {selectedRisk.riskSource}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <AlertFlagBadge flag={selectedRisk.flag} />
              </Descriptions.Item>
            </Descriptions>
          </motion.div>
        )}
      </Drawer>

      {/* Material Risk Interlinking Drawer */}
      <Drawer
        title={
          <span>
            <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            Material Risk Interlinking & Automated Mitigation
          </span>
        }
        placement="right"
        onClose={() => setMaterialInterlinkingDrawerVisible(false)}
        open={materialInterlinkingDrawerVisible}
        width="85%"
        style={{ maxWidth: 1400 }}
      >
        {selectedMaterialRisks.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Material Name" span={2}>
                <strong>{selectedMaterialRisks[0].materialName}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Delay Days">
                <Tag color="red" style={{ fontSize: 13, fontWeight: 600 }}>
                  {selectedMaterialRisks[0].delayDays} days
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Projects Affected">
                {selectedMaterialRisks[0].projectsAffected}
              </Descriptions.Item>
              <Descriptions.Item label="Tasks Affected">
                {selectedMaterialRisks[0].tasksAffected}
              </Descriptions.Item>
              <Descriptions.Item label="Work Orders Affected">
                {selectedMaterialRisks[0].wosAffected}
              </Descriptions.Item>
              <Descriptions.Item label="Downstream Impact" span={2}>
                {selectedMaterialRisks[0].downstreamImpact}
              </Descriptions.Item>
              <Descriptions.Item label="Triggered Module" span={2}>
                {selectedMaterialRisks[0].triggeredModule}
              </Descriptions.Item>
              <Descriptions.Item label="Monetary Impact" span={2}>
                <strong style={{ color: "#ff4d4f", fontSize: 14 }}>
                  {formatCurrency(
                    selectedMaterialRisks[0].monetaryImpact,
                    "INR"
                  )}
                </strong>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Material Shortage with Risk Interlinking</Divider>

            <Table
              columns={[
                {
                  title: "Material ID",
                  dataIndex: "materialId",
                  key: "materialId",
                  width: 100,
                  render: (text: string) => (
                    <span style={{ fontWeight: 600 }}>{text}</span>
                  ),
                },
                {
                  title: "Material Name",
                  dataIndex: "materialName",
                  key: "materialName",
                  width: 150,
                },
                {
                  title: "Delay (Days)",
                  dataIndex: "delayDays",
                  key: "delayDays",
                  width: 100,
                  align: "center" as const,
                  render: (val: number) => <Tag color="red">{val}</Tag>,
                },
                {
                  title: "Risk Type",
                  dataIndex: "riskType",
                  key: "riskType",
                  width: 130,
                  render: (text: string) => (
                    <Tag color="orange" style={{ fontSize: 11 }}>
                      {text}
                    </Tag>
                  ),
                },
                {
                  title: "Downstream Impact",
                  dataIndex: "downstreamImpact",
                  key: "downstreamImpact",
                  width: 180,
                },
                {
                  title: "Triggered Module",
                  dataIndex: "triggeredModule",
                  key: "triggeredModule",
                  width: 150,
                },
                {
                  title: "Monetary Impact",
                  dataIndex: "monetaryImpact",
                  key: "monetaryImpact",
                  width: 130,
                  align: "right" as const,
                  render: (val: number) => (
                    <span style={{ fontWeight: 600, color: "#ff4d4f" }}>
                      {formatCurrency(val, "INR")}
                    </span>
                  ),
                },
                {
                  title: "Responsible Person",
                  dataIndex: "responsiblePerson",
                  key: "responsiblePerson",
                  width: 140,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 100,
                  render: (status: string) => (
                    <Tag
                      color={
                        status === "Active"
                          ? "red"
                          : status === "Mitigated"
                          ? "green"
                          : "orange"
                      }
                    >
                      {status}
                    </Tag>
                  ),
                },
              ]}
              dataSource={selectedMaterialRisks}
              rowKey="materialId"
              pagination={false}
              scroll={{ x: 1200 }}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([record.materialId]);
                    setAiLoadingMaterials((prev) => ({
                      ...prev,
                      [record.materialId]: true,
                    }));
                    setAiLoadingTexts((prev) => ({
                      ...prev,
                      [record.materialId]: aiLoadingMessages[0],
                    }));

                    let messageIndex = 0;
                    const messageInterval = setInterval(() => {
                      messageIndex =
                        (messageIndex + 1) % aiLoadingMessages.length;
                      setAiLoadingTexts((prev) => ({
                        ...prev,
                        [record.materialId]: aiLoadingMessages[messageIndex],
                      }));
                    }, 800);

                    setTimeout(() => {
                      clearInterval(messageInterval);
                      setAiLoadingMaterials((prev) => ({
                        ...prev,
                        [record.materialId]: false,
                      }));
                    }, 2500);
                  } else {
                    setExpandedRowKeys([]);
                  }
                },
                expandedRowRender: (record) => (
                  <div
                    style={{
                      padding: "16px",
                      background: "#fafafa",
                      borderRadius: 8,
                    }}
                  >
                    <h6
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: "#262626",
                      }}
                    >
                      <RobotOutlined
                        style={{ marginRight: 6, color: "#1890ff" }}
                      />
                      Automated Mitigation
                    </h6>

                    {aiLoadingMaterials[record.materialId] ? (
                      <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <Spin
                          indicator={
                            <LoadingOutlined
                              style={{ fontSize: 32, color: "#1890ff" }}
                              spin
                            />
                          }
                        />
                        <p
                          style={{
                            marginTop: 16,
                            color: "#8c8c8c",
                            fontSize: 13,
                          }}
                        >
                          {aiLoadingTexts[record.materialId] || "Processing..."}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            marginBottom: 16,
                            padding: "12px 16px",
                            background: "#e6f7ff",
                            borderRadius: 6,
                            borderLeft: "3px solid #1890ff",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 13,
                              marginBottom: 0,
                              lineHeight: 1.6,
                              color: "#262626",
                            }}
                          >
                            <strong>Strategy:</strong>{" "}
                            {record.automatedMitigation}
                          </p>
                        </div>

                        {record.automatedMitigationSteps ? (
                          <div style={{ marginTop: 8 }}>
                            {record.automatedMitigationSteps.map(
                              (stepData, index) => (
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: 16,
                                    paddingBottom: 12,
                                    borderBottom:
                                      index <
                                      record.automatedMitigationSteps!.length -
                                        1
                                        ? "1px dashed #e8e8e8"
                                        : "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: 8,
                                      gap: 8,
                                    }}
                                  >
                                    {/* <Tag color="blue" style={{ fontSize: 12, fontWeight: 600 }}>{`Step ${index + 1}`}</Tag> */}
                                    <strong
                                      style={{ fontSize: 13, color: "#262626" }}
                                    >
                                      {stepData.step}
                                    </strong>
                                  </div>

                                  {stepData.siteTransfers && (
                                    <div
                                      style={{ marginTop: 8, marginLeft: 24 }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#595959",
                                          marginBottom: 8,
                                        }}
                                      >
                                        Available Sites for Transfer
                                      </div>
                                      {stepData.siteTransfers.map(
                                        (site, idx) => (
                                          <div
                                            key={idx}
                                            style={{
                                              padding: "10px 14px",
                                              marginBottom: 8,
                                              background: "#f0f5ff",
                                              borderRadius: 6,
                                              border: "1px solid #adc6ff",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: 4,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: 13,
                                                  fontWeight: 600,
                                                  color: "#262626",
                                                }}
                                              >
                                                {idx + 1}. {site.siteName}
                                              </span>
                                              <Tag
                                                color="green"
                                                style={{ fontWeight: 600 }}
                                              >
                                                Available
                                              </Tag>
                                            </div>
                                            <div
                                              style={{
                                                fontSize: 12,
                                                color: "#595959",
                                                marginTop: 6,
                                              }}
                                            >
                                              <div style={{ marginBottom: 2 }}>
                                                📦 Quantity:{" "}
                                                <strong>
                                                  {site.availableQty}
                                                </strong>
                                              </div>
                                              <div style={{ marginBottom: 2 }}>
                                                📍 Distance:{" "}
                                                <strong>{site.distance}</strong>
                                              </div>
                                              <div>
                                                ⏱️ Transfer Time:{" "}
                                                <strong>
                                                  {site.transferTime}
                                                </strong>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}

                                  {stepData.details && (
                                    <ul
                                      style={{
                                        margin: "8px 0 0 24px",
                                        padding: 0,
                                        listStyle: "disc",
                                      }}
                                    >
                                      {stepData.details.map((detail, idx) => (
                                        <li
                                          key={idx}
                                          style={{
                                            fontSize: 12,
                                            color: "#595959",
                                            marginBottom: 4,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {detail}
                                        </li>
                                      ))}
                                    </ul>
                                  )}

                                  {stepData.suppliers && (
                                    <div
                                      style={{ marginTop: 8, marginLeft: 24 }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#595959",
                                          marginBottom: 6,
                                        }}
                                      >
                                        Suggested Suppliers (Ranked by On-Time
                                        Performance)
                                      </div>
                                      {stepData.suppliers.map(
                                        (supplier, idx) => (
                                          <div
                                            key={idx}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              padding: "6px 12px",
                                              marginBottom: 6,
                                              background: "#f0f5ff",
                                              borderRadius: 4,
                                              border: "1px solid #d6e4ff",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                color: "#262626",
                                              }}
                                            >
                                              {idx + 1}. {supplier.name}
                                            </span>
                                            <Tag
                                              color={
                                                supplier.onTimePercentage >= 95
                                                  ? "green"
                                                  : supplier.onTimePercentage >=
                                                    90
                                                  ? "blue"
                                                  : "orange"
                                              }
                                              style={{ fontWeight: 600 }}
                                            >
                                              {supplier.onTimePercentage}%
                                              On-Time
                                            </Tag>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}

                                  {stepData.notifications && (
                                    <div
                                      style={{ marginTop: 8, marginLeft: 24 }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#595959",
                                          marginBottom: 6,
                                        }}
                                      >
                                        Actions & Notifications
                                      </div>
                                      <ul
                                        style={{
                                          margin: 0,
                                          padding: 0,
                                          listStyle: "none",
                                        }}
                                      >
                                        {stepData.notifications.map(
                                          (notification, idx) => (
                                            <li
                                              key={idx}
                                              style={{
                                                fontSize: 12,
                                                color: "#595959",
                                                marginBottom: 6,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                              }}
                                            >
                                              <BellOutlined
                                                style={{
                                                  fontSize: 12,
                                                  color: "#1890ff",
                                                }}
                                              />
                                              {notification}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                  {stepData.costSavings && (
                                    <div
                                      style={{
                                        marginTop: 8,
                                        marginLeft: 24,
                                        padding: "6px 12px",
                                        background: "#f6ffed",
                                        borderRadius: 4,
                                        border: "1px solid #b7eb8f",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#52c41a",
                                        }}
                                      >
                                        💰 Cost Savings: {stepData.costSavings}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p
                            style={{
                              fontSize: 13,
                              color: "#8c8c8c",
                              marginTop: 8,
                            }}
                          >
                            {record.automatedMitigation}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ),
              }}
            />
          </motion.div>
        )}
      </Drawer>

      {/* Invoice Payables Risk Drawer */}
      <Drawer
        title={
          <span>
            <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            Invoice Payment Risk & Automated Mitigation
          </span>
        }
        placement="right"
        onClose={() => setInvoiceRiskDrawerVisible(false)}
        open={invoiceRiskDrawerVisible}
        width="85%"
        style={{ maxWidth: 1400 }}
      >
        {selectedInvoiceRisk && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Invoice #" span={1}>
                <strong>{selectedInvoiceRisk.invoiceNumber}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="PO #" span={1}>
                <strong>{selectedInvoiceRisk.poNumber}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Supplier" span={2}>
                {selectedInvoiceRisk.supplier}
              </Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {dayjs(selectedInvoiceRisk.dueDate).format("DD-MMM-YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Aging Days">
                <Tag
                  color={
                    selectedInvoiceRisk.agingDays > 20
                      ? "red"
                      : selectedInvoiceRisk.agingDays > 10
                      ? "orange"
                      : "green"
                  }
                  style={{ fontSize: 13, fontWeight: 600 }}
                >
                  {selectedInvoiceRisk.agingDays} days
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Value">
                {formatCurrency(selectedInvoiceRisk.invoiceValue, "INR")}
              </Descriptions.Item>
              <Descriptions.Item label="Outstanding Value">
                <strong style={{ color: "#ff4d4f", fontSize: 14 }}>
                  {formatCurrency(selectedInvoiceRisk.outstandingValue, "INR")}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Interest Rate" span={2}>
                <Tag color="orange" style={{ fontSize: 13, fontWeight: 600 }}>
                  {selectedInvoiceRisk.interestRate}% per month
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Financial Exposure" span={2}>
                <strong style={{ color: "#ff4d4f", fontSize: 14 }}>
                  {formatCurrency(selectedInvoiceRisk.riskImpact, "INR")}
                </strong>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Invoice Payment Risk with Interlinking</Divider>

            <Table
              columns={[
                {
                  title: "Invoice #",
                  dataIndex: "invoiceNumber",
                  key: "invoiceNumber",
                  width: 130,
                  render: (text: string) => (
                    <span style={{ fontWeight: 600 }}>{text}</span>
                  ),
                },
                {
                  title: "PO #",
                  dataIndex: "poNumber",
                  key: "poNumber",
                  width: 120,
                },
                {
                  title: "Supplier",
                  dataIndex: "supplier",
                  key: "supplier",
                  width: 180,
                },
                {
                  title: "Due Date",
                  dataIndex: "dueDate",
                  key: "dueDate",
                  width: 120,
                  render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
                },
                {
                  title: "Aging (Days)",
                  dataIndex: "agingDays",
                  key: "agingDays",
                  width: 110,
                  align: "center" as const,
                  render: (val: number) => (
                    <Tag
                      color={val > 20 ? "red" : val > 10 ? "orange" : "green"}
                    >
                      {val}
                    </Tag>
                  ),
                },
                {
                  title: "Outstanding (₹)",
                  dataIndex: "outstandingValue",
                  key: "outstandingValue",
                  width: 140,
                  align: "right" as const,
                  render: (val: number) => (
                    <span style={{ fontWeight: 600, color: "#cf1322" }}>
                      {formatCurrency(val, "INR")}
                    </span>
                  ),
                },
                {
                  title: "Interest Charged",
                  dataIndex: "interestCharged",
                  key: "interestCharged",
                  width: 130,
                  align: "center" as const,
                  render: (val: number) => (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#fa8c16",
                      }}
                    >
                      {val.toFixed(2)}%
                    </span>
                  ),
                },
                {
                  title: "Risk Impact (₹)",
                  dataIndex: "riskImpact",
                  key: "riskImpact",
                  width: 130,
                  align: "right" as const,
                  render: (val: number) => (
                    <span style={{ fontWeight: 700, color: "#ff4d4f" }}>
                      {formatCurrency(val, "INR")}
                    </span>
                  ),
                },
                {
                  title: "Status",
                  dataIndex: "flag",
                  key: "flag",
                  width: 80,
                  align: "center" as const,
                  render: (flag: string) => (
                    <Tag
                      color={
                        flag === "🔴"
                          ? "red"
                          : flag === "🟠"
                          ? "orange"
                          : "green"
                      }
                      style={{ fontSize: 16 }}
                    >
                      {flag}
                    </Tag>
                  ),
                },
              ]}
              dataSource={invoicePayableRisks.filter(
                (inv) => inv.supplier === selectedInvoiceRisk.supplier
              )}
              rowKey="invoiceNumber"
              pagination={false}
              scroll={{ x: 1200 }}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([record.invoiceNumber]);
                    setAiLoadingMaterials((prev) => ({
                      ...prev,
                      [record.invoiceNumber]: true,
                    }));
                    setAiLoadingTexts((prev) => ({
                      ...prev,
                      [record.invoiceNumber]: aiLoadingMessages[0],
                    }));

                    let messageIndex = 0;
                    const messageInterval = setInterval(() => {
                      messageIndex =
                        (messageIndex + 1) % aiLoadingMessages.length;
                      setAiLoadingTexts((prev) => ({
                        ...prev,
                        [record.invoiceNumber]: aiLoadingMessages[messageIndex],
                      }));
                    }, 800);

                    setTimeout(() => {
                      clearInterval(messageInterval);
                      setAiLoadingMaterials((prev) => ({
                        ...prev,
                        [record.invoiceNumber]: false,
                      }));
                    }, 2500);
                  } else {
                    setExpandedRowKeys([]);
                  }
                },
                expandedRowRender: (record) => (
                  <div
                    style={{
                      padding: "16px",
                      background: "#fafafa",
                      borderRadius: 8,
                    }}
                  >
                    <h6
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: "#262626",
                      }}
                    >
                      <RobotOutlined
                        style={{ marginRight: 6, color: "#1890ff" }}
                      />
                      Automated Mitigation
                    </h6>

                    {aiLoadingMaterials[record.invoiceNumber] ? (
                      <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <Spin
                          indicator={
                            <LoadingOutlined
                              style={{ fontSize: 32, color: "#1890ff" }}
                              spin
                            />
                          }
                        />
                        <p
                          style={{
                            marginTop: 16,
                            color: "#8c8c8c",
                            fontSize: 13,
                          }}
                        >
                          {aiLoadingTexts[record.invoiceNumber] ||
                            "Processing..."}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            marginBottom: 16,
                            padding: "12px 16px",
                            background: "#e6f7ff",
                            borderRadius: 6,
                            borderLeft: "3px solid #1890ff",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 13,
                              marginBottom: 0,
                              lineHeight: 1.6,
                              color: "#262626",
                            }}
                          >
                            <strong>Strategy:</strong> Implement priority
                            payment workflow, supplier communication, and
                            process automation to avoid{" "}
                            {formatCurrency(record.riskImpact, "INR")} interest
                            penalty
                          </p>
                        </div>

                        <div style={{ marginTop: 8 }}>
                          {/* Step 1: Priority Payment Scheduling */}
                          <div
                            style={{
                              marginBottom: 16,
                              paddingBottom: 12,
                              borderBottom: "1px dashed #e8e8e8",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 8,
                                gap: 8,
                              }}
                            >
                              <strong
                                style={{ fontSize: 13, color: "#262626" }}
                              >
                                🔵 Step 1: Priority Payment Scheduling
                              </strong>
                            </div>

                            <ul
                              style={{
                                margin: "8px 0 0 24px",
                                padding: 0,
                                listStyle: "disc",
                              }}
                            >
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Auto-flag invoice for immediate payment
                                processing
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Move to top of payment queue (aging &gt;{" "}
                                {record.agingDays} days)
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Notify Finance Manager: Urgent payment approval
                                required
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Send automated reminder to CFO for cashflow
                                allocation
                              </li>
                            </ul>

                            <div style={{ marginTop: 8, marginLeft: 24 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#595959",
                                  marginBottom: 6,
                                }}
                              >
                                Actions & Notifications
                              </div>
                              <ul
                                style={{
                                  margin: 0,
                                  padding: 0,
                                  listStyle: "none",
                                }}
                              >
                                <li
                                  style={{
                                    fontSize: 12,
                                    color: "#595959",
                                    marginBottom: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <BellOutlined
                                    style={{
                                      fontSize: 12,
                                      color: "#1890ff",
                                    }}
                                  />
                                  Email to Finance Manager with payment priority
                                </li>
                                <li
                                  style={{
                                    fontSize: 12,
                                    color: "#595959",
                                    marginBottom: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <BellOutlined
                                    style={{
                                      fontSize: 12,
                                      color: "#1890ff",
                                    }}
                                  />
                                  Alert to CFO dashboard for cashflow review
                                </li>
                              </ul>
                            </div>

                            <div
                              style={{
                                marginTop: 8,
                                marginLeft: 24,
                                padding: "6px 12px",
                                background: "#f6ffed",
                                borderRadius: 4,
                                border: "1px solid #b7eb8f",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#52c41a",
                                }}
                              >
                                💰 Cost Savings: Avoid{" "}
                                {formatCurrency(record.riskImpact, "INR")}{" "}
                                interest penalty
                              </span>
                            </div>
                          </div>

                          {/* Step 2: Supplier Communication */}
                          <div
                            style={{
                              marginBottom: 16,
                              paddingBottom: 12,
                              borderBottom: "1px dashed #e8e8e8",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 8,
                                gap: 8,
                              }}
                            >
                              <strong
                                style={{ fontSize: 13, color: "#262626" }}
                              >
                                🟢 Step 2: Supplier Communication & Negotiation
                              </strong>
                            </div>

                            <ul
                              style={{
                                margin: "8px 0 0 24px",
                                padding: 0,
                                listStyle: "disc",
                              }}
                            >
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Send automated payment confirmation email to{" "}
                                {record.supplier}
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Request waiver of interest charges due to delay
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Offer early payment discount for future invoices
                                (2-3% on next PO)
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Update supplier on revised payment schedule
                              </li>
                            </ul>

                            <div style={{ marginTop: 8, marginLeft: 24 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#595959",
                                  marginBottom: 6,
                                }}
                              >
                                Actions & Notifications
                              </div>
                              <ul
                                style={{
                                  margin: 0,
                                  padding: 0,
                                  listStyle: "none",
                                }}
                              >
                                <li
                                  style={{
                                    fontSize: 12,
                                    color: "#595959",
                                    marginBottom: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <BellOutlined
                                    style={{
                                      fontSize: 12,
                                      color: "#1890ff",
                                    }}
                                  />
                                  Automated payment confirmation to{" "}
                                  {record.supplier}
                                </li>
                                <li
                                  style={{
                                    fontSize: 12,
                                    color: "#595959",
                                    marginBottom: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <BellOutlined
                                    style={{
                                      fontSize: 12,
                                      color: "#1890ff",
                                    }}
                                  />
                                  Negotiation request for interest waiver
                                </li>
                              </ul>
                            </div>

                            <div
                              style={{
                                marginTop: 8,
                                marginLeft: 24,
                                padding: "6px 12px",
                                background: "#f6ffed",
                                borderRadius: 4,
                                border: "1px solid #b7eb8f",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#52c41a",
                                }}
                              >
                                🤝 Maintain supplier relationship & trust for
                                continued supply
                              </span>
                            </div>
                          </div>

                          {/* Step 3: Process Improvement */}
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 8,
                                gap: 8,
                              }}
                            >
                              <strong
                                style={{ fontSize: 13, color: "#262626" }}
                              >
                                🟠 Step 3: Process Improvement & Alerts
                              </strong>
                            </div>

                            <ul
                              style={{
                                margin: "8px 0 0 24px",
                                padding: 0,
                                listStyle: "disc",
                              }}
                            >
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Set up automated aging alerts at 7, 15, and 30
                                days
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Create payment approval workflow with escalation
                                matrix
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Generate weekly aging report for all pending
                                invoices
                              </li>
                              <li
                                style={{
                                  fontSize: 12,
                                  color: "#595959",
                                  marginBottom: 4,
                                  lineHeight: 1.5,
                                }}
                              >
                                Review payment terms with {record.supplier} for
                                optimization
                              </li>
                            </ul>

                            <div style={{ marginTop: 8, marginLeft: 24 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#595959",
                                  marginBottom: 6,
                                }}
                              >
                                Actions & Notifications
                              </div>
                              <ul
                                style={{
                                  margin: 0,
                                  padding: 0,
                                  listStyle: "none",
                                }}
                              >
                                <li
                                  style={{
                                    fontSize: 12,
                                    color: "#595959",
                                    marginBottom: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <BellOutlined
                                    style={{
                                      fontSize: 12,
                                      color: "#1890ff",
                                    }}
                                  />
                                  Setup aging alerts for future invoices
                                </li>
                                <li
                                  style={{
                                    fontSize: 12,
                                    color: "#595959",
                                    marginBottom: 6,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <BellOutlined
                                    style={{
                                      fontSize: 12,
                                      color: "#1890ff",
                                    }}
                                  />
                                  Generate weekly payment aging dashboard
                                </li>
                              </ul>
                            </div>

                            <div
                              style={{
                                marginTop: 8,
                                marginLeft: 24,
                                padding: "6px 12px",
                                background: "#f6ffed",
                                borderRadius: 4,
                                border: "1px solid #b7eb8f",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#52c41a",
                                }}
                              >
                                📊 Prevent future delays & improve working
                                capital management
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ),
              }}
            />
          </motion.div>
        )}
      </Drawer>
    </>
  );
};

export default ProcurementInventoryControlTower;
